import React, { useState, useEffect } from 'react';
import { Button, Menu, MenuItem, Box, Typography } from '@mui/material';
import LanguageIcon       from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { useAuth }        from '../context/AuthContext';
import axios              from '../api/axiosConfig';

const LANGUAGES = [
    { code: 'en', label: 'English',  countryCode: 'gb' },
    { code: 'es', label: 'Español',  countryCode: 'es' },
    { code: 'ru', label: 'Русский',  countryCode: 'ru' },
    { code: 'hy', label: 'Հայերեն',  countryCode: 'am' },
];

/* Small flag component using flagcdn.com SVGs — renders identically on all platforms */
function Flag({ countryCode, size = 20 }) {
    return (
        <Box
            component="img"
            src={`https://flagcdn.com/w40/${countryCode}.png`}
            srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
            alt={countryCode}
            sx={{
                width: size,
                height: Math.round(size * 0.75),
                objectFit: 'cover',
                borderRadius: '2px',
                flexShrink: 0,
            }}
        />
    );
}

export default function LanguageSwitcher({ variant = 'button' }) {
    const { i18n }  = useTranslation();
    const { user, setUser }  = useAuth();
    const [anchor,  setAnchor]  = useState(null);
    const [langMap, setLangMap] = useState({});

    useEffect(() => {
        if (!user) return;
        axios.get('/languages/view')
            .then(res => {
                const map = {};
                (Array.isArray(res.data) ? res.data : (res.data?.content || [])).forEach(l => { if (l.code) map[l.code] = l.id; });
                setLangMap(map);
            })
            .catch(() => {});
    }, [user]);

    const activeLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

    const handleSelect = async (lang) => {
        setAnchor(null);

        await i18n.changeLanguage(lang.code);

        if (user && langMap[lang.code]) {
            try {
                const payload = {
                    username:    user.username   || '',
                    phone:       user.phone      || '',
                    idNumber:    user.idNumber   || '',
                    role:        user.role       || '',
                    removeImage: false,
                    languageId:  langMap[lang.code],
                };
                const form = new FormData();
                form.append('user', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
                const res = await axios.put(`/api/app_user/edit/${user.id}`, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                setUser(prev => ({ ...prev, languageId: langMap[lang.code], ...res.data }));
            } catch { /* silent — UI already updated */ }
        }
    };

    // Icon variant (compact, used in AppBar)
    if (variant === 'icon') {
        return (
            <>
                <Button onClick={e => setAnchor(e.currentTarget)} sx={{
                    minWidth: 0, px: 1, color: 'var(--text-main)',
                    fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                    '&:hover': { bgcolor: 'rgba(196,98,45,0.06)' },
                    gap: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <Flag countryCode={activeLang.countryCode} size={22} />
                    <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 700, color: 'var(--text-secondary)' }}>
                        {activeLang.code.toUpperCase()}
                    </Typography>
                </Button>
                <LangMenu anchor={anchor} onClose={() => setAnchor(null)}
                          onSelect={handleSelect} active={activeLang.code} />
            </>
        );
    }

    // Full button variant (used in Profile or standalone)
    return (
        <>
            <Button startIcon={<LanguageIcon sx={{ fontSize: '1rem' }} />}
                    onClick={e => setAnchor(e.currentTarget)}
                    sx={{
                        color: 'var(--text-main)', fontWeight: 600, fontSize: '0.82rem',
                        textTransform: 'none', borderRadius: 2,
                        border: '1px solid rgba(196,98,45,0.2)', px: 1.5, py: 0.6,
                        '&:hover': { borderColor: 'var(--primary-color)', bgcolor: 'rgba(196,98,45,0.04)' },
                    }}>
                <Flag countryCode={activeLang.countryCode} size={18} />
                <Box component="span" sx={{ ml: 1 }}>{activeLang.label}</Box>
            </Button>
            <LangMenu anchor={anchor} onClose={() => setAnchor(null)}
                      onSelect={handleSelect} active={activeLang.code} />
        </>
    );
}

function LangMenu({ anchor, onClose, onSelect, active }) {
    return (
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={onClose}
              PaperProps={{ elevation: 3, sx: {
                      mt: 0.5, borderRadius: 2,
                      border: '1px solid rgba(196,98,45,0.12)',
                      minWidth: 160, overflow: 'hidden',
                  }}}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={  { horizontal: 'right', vertical: 'bottom' }}>
            {LANGUAGES.map(lang => (
                <MenuItem key={lang.code} onClick={() => onSelect(lang)}
                          selected={lang.code === active}
                          sx={{
                              py: 1, px: 2,
                              fontWeight: lang.code === active ? 700 : 400,
                              color: lang.code === active ? 'var(--primary-color)' : 'inherit',
                              '&.Mui-selected': {
                                  bgcolor: 'rgba(196,98,45,0.06)',
                                  '&:hover': { bgcolor: 'rgba(196,98,45,0.10)' },
                              },
                          }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Flag countryCode={lang.countryCode} size={20} />
                        <Typography variant="body2" fontWeight="inherit">{lang.label}</Typography>
                        {lang.code === active && (
                            <Box sx={{ ml: 'auto', width: 6, height: 6, borderRadius: '50%',
                                bgcolor: 'var(--primary-color)' }} />
                        )}
                    </Box>
                </MenuItem>
            ))}
        </Menu>
    );
}